import { useEffect, useRef } from "react";

/**
 * Tour MODALS — how a walkthrough closes a modal it opened.
 *
 * A step's `action` can open a modal, but an action has no undo. So a modal a
 * tour may open carries a TAG, and registers its close while it is open:
 *
 *   <BdrModal tourTag={TAG} open={open} onClose={close} …>
 *   useTourModal(TAG, open, close)   // an overlay not on the shell
 *
 * (Written with TAG, not a quoted example: tourRegistry.test.js checks every
 * tag a step names exists as tourTag="…" / useTourModal("…" in client/src, and
 * a quoted example here would satisfy that check by itself.)
 *
 * A step names the modals it is shown inside (`modal: "create-event"`, or an
 * array). The engine keeps a modal the tour opened only while the current step
 * names it, and closes the rest on every step change and when the tour ends
 * (TourContext.jsx). It never closes a modal that was already open when the
 * tour started.
 *
 * RULE (TOUR_AUTHORING_GUIDE.md §5): a tag is a promise that calling this
 * close only changes UI state. If the modal's onClose saves, confirms,
 * navigates or writes, don't tag the shell — call useTourModal with a pure
 * close in the caller instead.
 *
 * INVARIANT: an entry leaves the registry only when its modal's effect cleans
 * up (closed / unmounted), never inside closeTourModal. The engine waits for
 * "no longer registered" before looking for the next anchor, so that wait ends
 * only once the modal is really gone from the DOM.
 */

// tag -> [{ close, seq }] (an array: two open instances of one tag must not
// overwrite each other's close).
const open = new Map();
let seq = 0;

export function registerTourModal(tag, close) {
  const entry = { close, seq: ++seq };
  open.set(tag, [...(open.get(tag) || []), entry]);
  return () => {
    const left = (open.get(tag) || []).filter((e) => e !== entry);
    if (left.length) open.set(tag, left);
    else open.delete(tag);
  };
}

export const isTourModalOpen = (tag) => open.has(tag);

/** Open tags, most recently opened first. */
export function openTourModalTags() {
  return [...open.entries()]
    .map(([tag, list]) => [tag, Math.max(...list.map((e) => e.seq))])
    .sort((a, b) => b[1] - a[1])
    .map(([tag]) => tag);
}

/** Close the newest open instance of `tag`. Never throws. */
export function closeTourModal(tag) {
  const list = open.get(tag);
  if (!list || !list.length) return false;
  const newest = list.reduce((a, b) => (b.seq > a.seq ? b : a));
  try {
    newest.close();
  } catch (err) {
    console.error(`[tours] closing modal "${tag}" failed:`, err);
  }
  return true;
}

/**
 * Close `tag` now if it is open, otherwise if it opens within `ms`. For the end
 * of a tour: a step's action may have opened a modal whose registration (a
 * passive effect) has not landed yet when the user presses Exit. Returns a
 * cancel function.
 */
export function closeIfOpensWithin(tag, ms) {
  if (closeTourModal(tag)) return () => {};
  const started = Date.now();
  const id = setInterval(() => {
    if (closeTourModal(tag) || Date.now() - started >= ms) clearInterval(id);
  }, 50);
  return () => clearInterval(id);
}

/**
 * Register `close` under `tag` while `isOpen`. A falsy tag registers nothing,
 * so BdrModal can call this unconditionally. The latest `close` is read
 * through a ref — callers pass inline arrows.
 */
export function useTourModal(tag, isOpen, close) {
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    if (!tag || !isOpen) return undefined;
    return registerTourModal(tag, () => closeRef.current?.());
  }, [tag, isOpen]);
}

// Test hook only.
export const _clearTourModals = () => {
  open.clear();
  seq = 0;
};
